import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { MyLocation, Place } from '@mui/icons-material';
import DialogCloseButton from './DialogCloseButton';
import { loadGoogleMaps } from '../../utils/googleMapsLoader';
import locationService from '../../services/locationService';

// Geographic centre of Zimbabwe — used when we have no better starting point.
const ZIMBABWE_CENTER = { lat: -19.0154, lng: 29.1549 };

/**
 * MapPinPicker — full-screen-ish dialog with an interactive satellite map and a
 * single draggable pin. Lets a user place a precise coordinate on a property that
 * Google cannot resolve from its address (common in Zimbabwe: new stands, informal
 * areas, unnumbered plots). They find the spot visually and drop the pin.
 *
 * Props:
 *   open            - whether the dialog is shown
 *   onClose         - called when dismissed without confirming
 *   onConfirm(pin)  - called with { lat, lng } when the user accepts the pin
 *   initialPin      - { lat, lng } to start the pin at (existing coordinates)
 *   initialCenter   - { lat, lng } to centre the map on when there is no pin yet
 *                     (e.g. the geocoded city the user typed)
 *   addressLabel    - text shown under the map for context
 */
const MapPinPicker = ({
  open,
  onClose,
  onConfirm,
  initialPin = null,
  initialCenter = null,
  addressLabel = '',
}) => {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [loadError, setLoadError] = useState('');
  const [loadingMap, setLoadingMap] = useState(true);
  const [pin, setPin] = useState(initialPin);
  const [locatingMe, setLocatingMe] = useState(false);

  const placeMarker = useCallback((maps, position) => {
    if (markerRef.current) {
      markerRef.current.setPosition(position);
    } else {
      markerRef.current = new maps.Marker({
        position,
        map: mapRef.current,
        draggable: true,
        title: 'Drag to your property',
      });
      markerRef.current.addListener('dragend', (e) => {
        setPin({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });
    }
    setPin({ lat: position.lat ?? position.lat(), lng: position.lng ?? position.lng() });
  }, []);

  // Build the map when the dialog opens. We intentionally rebuild on each open so
  // the map sizes correctly inside the freshly-mounted dialog content.
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoadingMap(true);
    setLoadError('');

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapElRef.current) return;

        const start = initialPin || initialCenter || ZIMBABWE_CENTER;
        const zoom = initialPin ? 18 : initialCenter ? 15 : 6;

        mapRef.current = new maps.Map(mapElRef.current, {
          center: start,
          zoom,
          mapTypeId: 'hybrid', // satellite imagery + street labels
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
        });

        // Clicking the map drops / moves the pin.
        mapRef.current.addListener('click', (e) => {
          placeMarker(maps, { lat: e.latLng.lat(), lng: e.latLng.lng() });
        });

        if (initialPin) {
          placeMarker(maps, initialPin);
        }
        setLoadingMap(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.message?.includes('API key')
            ? 'Map is unavailable (no map key configured). Enter coordinates manually instead.'
            : 'Map could not be loaded. Check your connection or enter coordinates manually.'
        );
        setLoadingMap(false);
      });

    return () => {
      cancelled = true;
      markerRef.current = null;
      mapRef.current = null;
    };
  }, [open, initialPin, initialCenter, placeMarker]);

  const handleUseMyLocation = async () => {
    setLocatingMe(true);
    try {
      const result = await locationService.getCurrentLocation();
      const { lat, lng } = result.data;
      const maps = window.google?.maps;
      if (maps && mapRef.current) {
        mapRef.current.setCenter({ lat, lng });
        mapRef.current.setZoom(18);
        placeMarker(maps, { lat, lng });
      }
    } catch (err) {
      // Non-fatal: the user can still pin manually.
      console.warn('Could not get current location:', err?.message);
    } finally {
      setLocatingMe(false);
    }
  };

  const handleConfirm = () => {
    if (pin) onConfirm?.({ lat: Number(pin.lat), lng: Number(pin.lng) });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>Pin your property location</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Pan and zoom to your property on the satellite map, then tap or drag the
          pin onto the exact spot. This works even when the address isn&apos;t in
          Google.
        </Typography>

        {loadError ? (
          <Alert severity="warning">{loadError}</Alert>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <Box
              ref={mapElRef}
              sx={{ height: { xs: 320, sm: 440 }, width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100' }}
            />
            {loadingMap && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            )}

            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
              <Button
                size="small"
                startIcon={locatingMe ? <CircularProgress size={14} /> : <MyLocation />}
                onClick={handleUseMyLocation}
                disabled={locatingMe || loadingMap}
              >
                Use my current location
              </Button>
              {pin ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Place fontSize="inherit" /> {Number(pin.lat).toFixed(6)}, {Number(pin.lng).toFixed(6)}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Tap the map to drop a pin
                </Typography>
              )}
            </Box>

            {addressLabel && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {addressLabel}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!pin}>
          Use this location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapPinPicker;
